import {Helper} from '@ui/atoms/helper';
import {Editor} from 'react-draft-wysiwyg';
import {convertFromRaw, convertToRaw, EditorState} from 'draft-js';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {useState} from 'react';
import styled from 'styled-components';
import {fixMarkdownBoldFormatting} from '@utils/strings';
import {markdownToDraft, draftToMarkdown} from 'markdown-draft-js';
import {RichTextToolbar} from '@ui/molecules/editor/rich-text-toolbar';

const Wrapper = styled.div<{error?: boolean}>`
  overflow: hidden;
  border: 1px solid
    ${({error, theme}) =>
      error ? theme.colors.red400 : theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  font-size: 16px;

  .input {
    padding: 0;
  }

  .custom-editor-wrapper {
    min-height: 144px;
    background: ${({theme}) => theme.colors.white};
    color: ${({theme}) => theme.colors.blackTwo};
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
  }

  .rdw-editor-toolbar.feedback-rich-text-toolbar {
    box-sizing: border-box;
    width: 100%;
    min-height: 48px;
    margin: 0;
    padding: 8px 10px;
    border: 0;
    border-bottom: 1px solid ${({theme}) => theme.colors.borderLight};
    border-radius: 10px 10px 0 0;
    background: ${({theme}) => theme.colors.white};
  }

  .feedback-rich-text-input {
    box-sizing: border-box;
    min-height: 96px;
    max-height: 300px;
    padding: 12px 10px;
    overflow-y: auto;
    background: ${({theme}) => theme.colors.white};
    color: ${({theme}) => theme.colors.blackTwo};
    font-size: 16px;
    line-height: 24px;
  }

  .feedback-rich-text-input .public-DraftEditorPlaceholder-root,
  .feedback-rich-text-input .public-DraftEditorPlaceholder-inner {
    color: ${({theme}) => theme.colors.textBody};
    font-size: 14px;
    line-height: 22px;
  }

  &:focus-within {
    border-color: ${({error, theme}) =>
      error ? theme.colors.red400 : theme.colors.purple300};
  }
`;

const FeedbackMeta = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: 8px;
  color: ${({theme}) => theme.colors.textBody};
  font-family: Graphik, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
`;

const MAX_FEEDBACK_CHARACTERS = 500;

type IFeedBackInput = {
  updateFeedbackInput: (value: string) => void;
  error?: string;
  placeholder?: string;
};
const FeedBackInput = ({
  updateFeedbackInput,
  error,
  placeholder = 'Make a difference with feedback that’s at least 30 characters.',
}: IFeedBackInput) => {
  const [editorState, setEditorState] = useState<any>(
    EditorState.createEmpty(),
  );
  const characterCount = editorState.getCurrentContent().getPlainText().length;

  const convertToDraft = (text: string) => {
    const rawData = markdownToDraft(text);

    const contentState = convertFromRaw(rawData);

    const newEditorState = EditorState.createWithContent(contentState);

    onEditorStateChange(newEditorState);
  };

  const onEditorStateChange = (editorState: EditorState) => {
    if (
      editorState.getCurrentContent().getPlainText().length >
      MAX_FEEDBACK_CHARACTERS
    ) {
      return;
    }

    const value = convertToRaw(editorState.getCurrentContent());
    const markdownContent = draftToMarkdown(value);

    updateFeedbackInput(fixMarkdownBoldFormatting(markdownContent));

    setEditorState(editorState);
  };

  return (
    <>
      <Wrapper data-testid="feedback-rich-text-editor" error={!!error}>
        <div className="input">
          <Editor
            wrapperStyle={{
              minHeight: '120px',
              maxHeight: '350px',
            }}
            wrapperClassName="custom-editor-wrapper"
            toolbarClassName="feedback-rich-text-toolbar"
            editorClassName="feedback-rich-text-input"
            ariaLabel="Feedback message"
            editorState={editorState}
            handlePastedText={(text) => {
              convertToDraft(text.slice(0, MAX_FEEDBACK_CHARACTERS));
              return true;
            }}
            placeholder={placeholder}
            onEditorStateChange={onEditorStateChange}
            toolbar={{
              options: [],
            }}
            toolbarCustomButtons={[
              <RichTextToolbar
                key="feedback-rich-text-toolbar"
                editorState={editorState}
                onChange={onEditorStateChange}
                showUnderline
                showStrikethrough
                showLink={false}
                className="feedback-rich-text-controls"
              />,
            ]}
          />
        </div>
      </Wrapper>
      <FeedbackMeta data-testid="feedback-character-meta">
        <span>30 characters minimum</span>
        <span aria-live="polite">
          {characterCount} / {MAX_FEEDBACK_CHARACTERS}
        </span>
      </FeedbackMeta>
      {!!error && <Helper children={error} state="error" />}
    </>
  );
};

export default FeedBackInput;
